import React, { useState, useCallback, useMemo } from 'react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  Image, Save, Search, Languages, Loader2, Plus, Trash2, Pencil,
  Eye, EyeOff, XCircle, CheckCircle, Package, GripVertical
} from 'lucide-react';
import { ImageUploader } from '../../components/ImageUploader';
import axios from 'axios';

import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  rectSortingStrategy,
  useSortable,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SpiceSelector = ({ value, onChange }) => (
  <div className="flex items-center gap-1.5">
    {[0, 1, 2, 3].map(level => (
      <button key={level} type="button" onClick={() => onChange(level)}
        className={`flex items-center gap-0.5 px-2.5 py-1.5 rounded-lg border-2 text-xs font-medium transition-all ${
          value === level ? 'border-[#E16833] bg-[#E16833]/10 text-[#E16833]' : 'border-[#E5DCD3] text-[#6E5E52] hover:border-[#E16833]/40'
        }`} data-testid={`spice-${level}`}>
        {level === 0 ? 'Doux' : '🌶'.repeat(level)}
      </button>
    ))}
  </div>
);

const SpiceBadge = ({ level }) => {
  if (!level || level === 0) return null;
  return <span className="text-[10px]">{'🌶'.repeat(level)}</span>;
};

const SortableMenuItem = ({ item, idx, categories, adminRole, formatPrice, toggleStock, setEditingItem, setEditDialogOpen }) => {
  const {
    attributes, listeners, setNodeRef, transform, transition, isDragging,
  } = useSortable({ id: item.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
    zIndex: isDragging ? 50 : 'auto',
  };

  return (
    <div ref={setNodeRef} style={style}
      className={`bg-white rounded-2xl p-4 border ${item.is_active === false ? 'border-red-300 opacity-60' : 'border-[#E5DCD3]'} ${isDragging ? 'shadow-xl ring-2 ring-[#D4A853]' : ''}`}
      data-testid={`menu-item-admin-${item.id}`}>
      <div className="flex gap-3">
        {/* Drag handle */}
        <div {...attributes} {...listeners}
          className="flex items-center justify-center cursor-grab active:cursor-grabbing touch-none px-0.5"
          data-testid={`drag-handle-${item.id}`}>
          <GripVertical className="w-5 h-5 text-[#D4A853]" />
        </div>
        <div className="relative w-20 h-20 flex-shrink-0">
          <div className="w-full h-full rounded-xl bg-[#F3EDE5] flex items-center justify-center overflow-hidden"
            style={item.image_url ? { backgroundImage: `url(${item.image_url})`, backgroundSize: 'cover', backgroundPosition: 'center' } : {}}>
            {!item.image_url && <span className="text-xl font-bold text-[#D4A853]">{item.name?.charAt(0)}</span>}
          </div>
          {item.is_popular && (
            <span className="absolute -top-1 -right-1 w-5 h-5 bg-[#E16833] rounded-full flex items-center justify-center text-white text-[10px]">P</span>
          )}
          {item.is_active === false && (
            <span className="absolute inset-0 bg-red-500/20 rounded-xl flex items-center justify-center">
              <XCircle className="w-8 h-8 text-red-500" />
            </span>
          )}
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-1">
            <h4 className="font-semibold text-[#2D241D] text-sm line-clamp-1">{item.name}</h4>
            <SpiceBadge level={item.spice_level} />
          </div>
          <p className="text-xs text-[#6E5E52] line-clamp-1">
            {categories.find(c => c.id === (item.category_id || item.category))?.name_fr || item.category}
          </p>
          <p className="text-[#E16833] font-bold text-sm mt-1">{formatPrice(item.price)}</p>
          {adminRole === 'admin' && item.cost_price > 0 && (
            <p className="text-[10px] text-[#6E5E52]">Cout: {formatPrice(item.cost_price)} | Marge: {Math.round((1 - item.cost_price / item.price) * 100)}%</p>
          )}
          {item.stock_count !== null && item.stock_count !== undefined && (
            <p className={`text-[10px] font-semibold flex items-center gap-0.5 ${item.stock_count <= 3 ? 'text-red-500' : 'text-[#2D6A4F]'}`}>
              <Package className="w-3 h-3" /> Stock: {item.stock_count}
            </p>
          )}
          {item.is_active === false && <p className="text-xs text-red-500 font-semibold">EPUISE</p>}
        </div>
      </div>
      <div className="flex gap-2 mt-3">
        <Button onClick={() => toggleStock(item.id)}
          variant="outline" size="sm"
          className={`flex-1 rounded-xl text-xs ${item.is_active === false
            ? 'border-[#2D6A4F] text-[#2D6A4F] hover:bg-[#2D6A4F] hover:text-white'
            : 'border-red-400 text-red-500 hover:bg-red-500 hover:text-white'}`}
          data-testid={`toggle-stock-${item.id}`}>
          {item.is_active === false ? <><Eye className="w-3 h-3 mr-1" /> Remettre</> : <><EyeOff className="w-3 h-3 mr-1" /> Epuise</>}
        </Button>
        <Button onClick={() => {
            setEditingItem({
              ...item, newImageUrl: item.image_url,
              newDescriptionFr: item.description_fr || '',
              newPrice: item.price, newCostPrice: item.cost_price || 0,
              newName: item.name, newCategoryId: item.category_id || item.category,
              newStockCount: item.stock_count,
              newSpiceLevel: item.spice_level || 0,
              translationSuccess: false
            });
            setEditDialogOpen(true);
          }}
          variant="outline" size="sm"
          className="flex-1 rounded-xl border-[#E5DCD3] text-[#6E5E52] text-xs hover:bg-[#0A422D] hover:text-white hover:border-[#0A422D]"
          data-testid={`edit-item-${item.id}`}>
          <Pencil className="w-3 h-3 mr-1" /> Modifier
        </Button>
      </div>
    </div>
  );
};

export const MenuTab = ({
  menuItems, categories, adminRole, menuSearch, setMenuSearch,
  menuCategory, setMenuCategory, filteredMenuItems,
  showNewItem, setShowNewItem, newItem, setNewItem,
  createMenuItem, editingItem, setEditingItem,
  editDialogOpen, setEditDialogOpen, saveMenuItem,
  deleteMenuItem, toggleStock, translateDescription,
  isTranslating, formatPrice, onRefresh,
}) => {
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 8 } }),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );

  const sortedItems = useMemo(() =>
    [...filteredMenuItems].sort((a, b) => (a.display_order || 999) - (b.display_order || 999)),
    [filteredMenuItems]
  );

  const itemIds = useMemo(() => sortedItems.map(i => i.id), [sortedItems]);

  const handleDragEnd = useCallback(async (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = sortedItems.findIndex(i => i.id === active.id);
    const newIndex = sortedItems.findIndex(i => i.id === over.id);
    if (oldIndex === -1 || newIndex === -1) return;

    const reordered = arrayMove(sortedItems, oldIndex, newIndex);
    const payload = reordered.map((item, i) => ({ id: item.id, display_order: i }));
    try {
      await axios.post(`${API}/admin/menu/reorder`, payload);
      onRefresh?.();
    } catch (e) { console.error(e); }
  }, [sortedItems, onRefresh]);

  return (
    <>
      <div className="bg-white rounded-2xl p-4 border border-[#E5DCD3] mb-4">
        <div className="flex flex-col md:flex-row gap-3">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-[#6E5E52]" />
            <Input type="text" placeholder="Rechercher..." value={menuSearch}
              onChange={(e) => setMenuSearch(e.target.value)}
              className="pl-10 rounded-xl border-[#E5DCD3]" data-testid="menu-search" />
          </div>
          <select value={menuCategory} onChange={(e) => setMenuCategory(e.target.value)}
            className="h-10 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm" data-testid="menu-category-filter">
            <option value="all">Toutes les categories</option>
            {categories.map(cat => (
              <option key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</option>
            ))}
          </select>
          <Dialog open={showNewItem} onOpenChange={setShowNewItem}>
            <DialogTrigger asChild>
              <Button className="bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl" data-testid="add-menu-item-btn">
                <Plus className="w-4 h-4 mr-2" /> Nouveau plat
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg bg-white rounded-3xl max-h-[90vh] overflow-y-auto admin-theme">
              <DialogHeader><DialogTitle className="text-[#2D241D]">Ajouter un plat</DialogTitle></DialogHeader>
              <div className="space-y-4 p-4">
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom du plat</label>
                  <Input value={newItem.name} onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                    placeholder="ex: Jerk Chicken" className="rounded-xl border-[#E5DCD3]" data-testid="new-item-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Description</label>
                  <Textarea value={newItem.description} onChange={(e) => setNewItem({ ...newItem, description: e.target.value })}
                    placeholder="Description du plat..." className="rounded-xl border-[#E5DCD3]" data-testid="new-item-desc" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-sm font-medium text-[#2D241D] mb-1 block">Prix (THB)</label>
                    <Input type="number" value={newItem.price} onChange={(e) => setNewItem({ ...newItem, price: e.target.value })}
                      placeholder="300" className="rounded-xl border-[#E5DCD3]" data-testid="new-item-price" />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-[#2D241D] mb-1 block">Categorie</label>
                    <select value={newItem.category_id} onChange={(e) => setNewItem({ ...newItem, category_id: e.target.value })}
                      className="w-full h-10 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm" data-testid="new-item-category">
                      <option value="">Choisir...</option>
                      {categories.map(cat => (
                        <option key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  {adminRole === 'admin' && (
                    <div>
                      <label className="text-sm font-medium text-[#2D241D] mb-1 block">Prix coutant (THB)</label>
                      <Input type="number" value={newItem.cost_price} onChange={(e) => setNewItem({ ...newItem, cost_price: e.target.value })}
                        placeholder="0" className="rounded-xl border-[#E5DCD3]" data-testid="new-item-cost-price" />
                    </div>
                  )}
                  <div>
                    <label className="text-sm font-medium text-[#2D241D] mb-1 block flex items-center gap-1">
                      <Package className="w-3.5 h-3.5" /> Stock (vide=illimite)
                    </label>
                    <Input type="number" value={newItem.stock_count ?? ''} onChange={(e) => setNewItem({ ...newItem, stock_count: e.target.value === '' ? null : parseInt(e.target.value) })}
                      placeholder="illimite" className="rounded-xl border-[#E5DCD3]" data-testid="new-item-stock" />
                  </div>
                </div>
                <ImageUploader value={newItem.image_url}
                  onChange={(url) => setNewItem({ ...newItem, image_url: url })}
                  label="Photo du plat" />
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Niveau de piment</label>
                  <SpiceSelector value={newItem.spice_level || 0} onChange={(v) => setNewItem({ ...newItem, spice_level: v })} />
                </div>
                <Button onClick={createMenuItem}
                  disabled={!newItem.name || !newItem.price || !newItem.category_id}
                  className="w-full bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl" data-testid="save-new-item-btn">
                  <Save className="w-4 h-4 mr-2" /> Creer le plat
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
        <div className="flex items-center justify-between mt-2">
          <p className="text-sm text-[#6E5E52]">{filteredMenuItems.length} articles</p>
          <p className="text-xs text-[#D4A853] flex items-center gap-1">
            <GripVertical className="w-3.5 h-3.5" /> Glissez pour reorganiser
          </p>
        </div>
      </div>

      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={itemIds} strategy={rectSortingStrategy}>
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {sortedItems.map((item, idx) => (
              <SortableMenuItem
                key={item.id}
                item={item}
                idx={idx}
                categories={categories}
                adminRole={adminRole}
                formatPrice={formatPrice}
                toggleStock={toggleStock}
                setEditingItem={setEditingItem}
                setEditDialogOpen={setEditDialogOpen}
              />
            ))}
          </div>
        </SortableContext>
      </DndContext>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onOpenChange={(open) => { if (!open) { setEditDialogOpen(false); setEditingItem(null); } }}>
        <DialogContent className="max-w-lg bg-white rounded-3xl max-h-[90vh] overflow-y-auto admin-theme">
          <DialogHeader>
            <DialogTitle className="text-lg text-[#2D241D]">{editingItem?.newName || ''}</DialogTitle>
          </DialogHeader>
          {editingItem && (
            <div className="space-y-5 p-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Nom</label>
                  <Input value={editingItem.newName}
                    onChange={(e) => setEditingItem({ ...editingItem, newName: e.target.value })}
                    className="rounded-xl border-[#E5DCD3] text-sm" data-testid="edit-item-name" />
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block">Prix (THB)</label>
                  <Input type="number" value={editingItem.newPrice}
                    onChange={(e) => setEditingItem({ ...editingItem, newPrice: parseFloat(e.target.value) || 0 })}
                    className="rounded-xl border-[#E5DCD3] text-sm" data-testid="edit-item-price" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                {adminRole === 'admin' && (
                  <div>
                    <label className="text-sm font-medium text-[#2D241D] mb-1 block">Prix coutant (THB)</label>
                    <Input type="number" value={editingItem.newCostPrice}
                      onChange={(e) => setEditingItem({ ...editingItem, newCostPrice: parseFloat(e.target.value) || 0 })}
                      className="rounded-xl border-[#E5DCD3] text-sm" data-testid="edit-item-cost-price" />
                    {editingItem.newCostPrice > 0 && editingItem.newPrice > 0 && (
                      <p className="text-xs text-[#2D6A4F] mt-1">
                        Marge: {Math.round((1 - editingItem.newCostPrice / editingItem.newPrice) * 100)}% ({formatPrice(editingItem.newPrice - editingItem.newCostPrice)} / unite)
                      </p>
                    )}
                  </div>
                )}
                <div>
                  <label className="text-sm font-medium text-[#2D241D] mb-1 block flex items-center gap-1">
                    <Package className="w-3.5 h-3.5" /> Stock (vide=illimite)
                  </label>
                  <Input type="number" value={editingItem.newStockCount ?? ''}
                    onChange={(e) => setEditingItem({ ...editingItem, newStockCount: e.target.value === '' ? null : parseInt(e.target.value) })}
                    placeholder="illimite" className="rounded-xl border-[#E5DCD3] text-sm" data-testid="edit-item-stock" />
                </div>
              </div>
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Categorie</label>
                <select value={editingItem.newCategoryId}
                  onChange={(e) => setEditingItem({ ...editingItem, newCategoryId: e.target.value })}
                  className="w-full h-10 rounded-xl border border-[#E5DCD3] px-3 bg-white text-sm"
                  data-testid="edit-item-category">
                  {categories.map(cat => (
                    <option key={cat.id} value={cat.id}>{cat.name_fr || cat.name}</option>
                  ))}
                </select>
              </div>
              <ImageUploader value={editingItem.newImageUrl}
                onChange={(url) => setEditingItem({ ...editingItem, newImageUrl: url })}
                label="Photo du plat" />
              <div>
                <label className="text-sm font-medium text-[#2D241D] mb-1 block">Niveau de piment</label>
                <SpiceSelector value={editingItem.newSpiceLevel || 0} onChange={(v) => setEditingItem({ ...editingItem, newSpiceLevel: v })} />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={editingItem.is_popular || false}
                  onChange={(e) => setEditingItem({ ...editingItem, is_popular: e.target.checked })}
                  className="w-4 h-4 rounded" />
                <span className="text-sm text-[#2D241D]">Populaire (affiche en vedette)</span>
              </label>
              <Button onClick={() => {
                const data = {
                  name: editingItem.newName, price: editingItem.newPrice,
                  category_id: editingItem.newCategoryId, image_url: editingItem.newImageUrl,
                  is_popular: editingItem.is_popular,
                  stock_count: editingItem.newStockCount,
                  spice_level: editingItem.newSpiceLevel || 0,
                };
                if (adminRole === 'admin') data.cost_price = editingItem.newCostPrice || 0;
                saveMenuItem(editingItem.id, data);
              }}
                className="w-full bg-[#0A422D] hover:bg-[#083323] text-white rounded-xl" data-testid="save-item-btn">
                <Save className="w-4 h-4 mr-2" /> Sauvegarder
              </Button>
              <div className="border-t border-[#E5DCD3] pt-4">
                <label className="text-sm font-medium text-[#2D241D] mb-2 block flex items-center gap-2">
                  <Languages className="w-4 h-4" /> Description (redigez en francais)
                </label>
                <Textarea placeholder="Ecrivez la description du plat en francais..."
                  value={editingItem.newDescriptionFr}
                  onChange={(e) => setEditingItem({ ...editingItem, newDescriptionFr: e.target.value, translationSuccess: false })}
                  className="rounded-xl border-[#E5DCD3] min-h-[80px] text-sm" data-testid="description-input" />
                <p className="text-xs text-[#6E5E52] mt-1">Traduite automatiquement dans 8 langues</p>
                <Button onClick={() => translateDescription(editingItem.id, editingItem.newDescriptionFr)}
                  disabled={isTranslating || !editingItem.newDescriptionFr?.trim()}
                  className="mt-3 w-full bg-[#E16833] hover:bg-[#C95A2A] text-white rounded-xl" data-testid="translate-btn">
                  {isTranslating ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" />Traduction en cours...</>
                  ) : (
                    <><Languages className="w-4 h-4 mr-2" />Valider et Traduire</>
                  )}
                </Button>
                {editingItem.translationSuccess && (
                  <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-xl">
                    <p className="text-green-700 text-sm font-medium flex items-center gap-2">
                      <CheckCircle className="w-4 h-4" />Traduction reussie !
                    </p>
                  </div>
                )}
                {editingItem.description_translations && Object.keys(editingItem.description_translations).length > 0 && (
                  <div className="mt-3 space-y-1">
                    <p className="text-xs font-medium text-[#6E5E52]">Traductions :</p>
                    <div className="max-h-32 overflow-y-auto space-y-1 text-xs bg-[#F3EDE5] rounded-xl p-3">
                      {Object.entries(editingItem.description_translations).map(([lang, text]) => (
                        <div key={lang} className="flex gap-2">
                          <span className="font-medium uppercase w-6 text-[#0A422D]">{lang}:</span>
                          <span className="text-[#6E5E52]">{text}</span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>
              {adminRole === 'admin' && (
                <Button onClick={() => { deleteMenuItem(editingItem.id); setEditDialogOpen(false); setEditingItem(null); }} variant="outline"
                  className="w-full rounded-xl border-[#E63946] text-[#E63946] hover:bg-[#E63946] hover:text-white"
                  data-testid="delete-item-btn">
                  <Trash2 className="w-4 h-4 mr-2" /> Supprimer ce plat
                </Button>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {filteredMenuItems.length === 0 && (
        <div className="text-center py-12 text-[#6E5E52]">
          <Image className="w-12 h-12 mx-auto mb-3 opacity-30" />
          <p>Aucun article trouve</p>
        </div>
      )}
    </>
  );
};
